﻿import React, { useState, useEffect } from 'react';
import { NavLink } from "react-router-dom";
import { NoteObject } from '../models/note-object';
import { NoteType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import NotesEditor from './NoteEditor';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';

import HtmlEditor from 'devextreme-react/html-editor';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';
import Resizable from 'devextreme-react/resizable';

export interface Props {
    noteType?: NoteType;
    parentId?: string;
    height?: string;
    title?: string
    readOnly?: boolean;
    headerColor?: string;
    resizable?: boolean;
}

export default function NoteViewer(props: Props) {
    const [note, setNote] =
        useState<NoteObject>();

    const [noteEditorDialogVisible, setNoteEditorDialogVisible] =
        useState<boolean>();

    const [resizableClasses, setResizableClasses]
        = useState('');

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getNote();
    }, [props.parentId]);

    return (
        <div id="note-viewer-root">
            <div className="lq-header pw-margin-top-05 aurum-blue-bg">
                <div className="lq-header-title">{props.title ? props.title : 'Notes'}</div>
                <div className="title-bar-spacer" />

                {props.parentId && props.noteType &&
                    <NavLink to={`/note-viewer-fp/${props.parentId}/${props.noteType}`}
                        className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                        <Button text="" icon="panelleft" type="success"
                            className="title-bar-button"
                            hint="open in new tab" />
                    </NavLink>
                }

                <div>
                    <Button className="title-bar-button"
                        text="" icon="pdffile" type="success"
                        hint="export to pdf"
                        onClick={exportToPdf} />
                </div>

                {!props.readOnly &&
                    <div>
                        <Button className="title-bar-button"
                            text="" icon="edit" type="success"
                            hint="edit note"
                            onClick={showNoteEditorDialog} />
                    </div>
                }
            </div>

            <div id="NoteViewerContainer" className="pw-form-section">
                {props.resizable &&
                    <Resizable minHeight={300} maxHeight={1000}
                        className="top no-left-handle no-right-handle"
                        handles="top bottom" keepAspectRatio={false}
                        area="#NoteViewerContainer">
                        <HtmlEditor height="100%" readOnly={true}
                            value={note && note.text ? note.text : undefined} />
                    </Resizable>
                }

                {!props.resizable &&
                    <HtmlEditor height={props.height ? props.height : "100%"} readOnly={true}
                        value={note && note.text ? note.text : undefined} />
                }
            </div>

            {noteEditorDialogVisible &&
                <DialogPopup
                    visible={noteEditorDialogVisible}
                    onHiding={hideNoteEditorDialog}
                    dragEnabled={true}
                    fullScreen={true}
                    hideOnOutsideClick={false}
                    title={props.title ? props.title : "Note Editor"} >
                    <NotesEditor parentId={props.parentId}
                        noteType={props.noteType}
                        onCancel={hideNoteEditorDialog} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function showNoteEditorDialog() {
        setNoteEditorDialogVisible(true);
    }

    function hideNoteEditorDialog() {
        setNoteEditorDialogVisible(false);
        getNote();
    }

    async function exportToPdf() {
        if (props.parentId && props.noteType) {
            await CrudFunctions.getDataBlob(`notes/notetopdf/${props.parentId}/${props.noteType}`)
                .then(blob => {
                    let a = document.createElement("a") as HTMLAnchorElement;

                    document.body.appendChild(a);
                    a.style.display = "none";

                    var url = window.URL.createObjectURL(blob);
                    a.href = url;
                    a.download = `${props.title ? props.title : 'note'}.pdf`;
                    a.click();
                    window.URL.revokeObjectURL(url);

                    /*
                    //to open PDF in browser:
                    let file = window.URL.createObjectURL(blob);
                    window.open(file, "_blank");
                    */
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Note");
                });
        }
    }

    // get functions

    async function getNote() {
        if (props.parentId && props.noteType) {
            await CrudFunctions.getData(`notes/notesbyparentidandnotetype/${props.parentId}/${props.noteType}`)
                .then(data => {
                    if (data && data.length > 0) {
                        let returnedNote = data[0] as NoteObject;

                        if (returnedNote) {
                            returnedNote.text = atob(returnedNote.base64Blob);
                            setNote(returnedNote);
                        }
                    }
                    else
                        setNote(null);
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Retrieving Note");
                });
        }
        else
            setNote(null);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

